import { Link } from "react-router-dom";
import { m as motion } from "framer-motion";
import { Calendar, Clock, ArrowRight, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BlurImage } from "@/components/ui/blur-image";
import { HtmlContent } from "@/components/ui/html-content";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";

export function FeaturedBlogSection() {
  const { data: latestPost, isLoading } = useQuery({
    queryKey: ["latest-blog-post"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, title, slug, excerpt, featured_image, featured_image_width, featured_image_height, featured_image_fit, category, published_at, read_time")
        .eq("is_published", true)
        .order("published_at", { ascending: false })
        .limit(1)
        .single();

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <section className="py-16 md:py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Skeleton className="h-10 w-64 mx-auto" />
            <Skeleton className="h-6 w-96 mx-auto mt-4" />
          </div>
          <div className="max-w-4xl mx-auto">
            <Skeleton className="aspect-video rounded-xl" />
            <Skeleton className="h-8 w-3/4 mt-6" />
            <Skeleton className="h-4 w-full mt-4" />
          </div>
        </div>
      </section>
    );
  }

  if (!latestPost) return null;

  const formattedDate = new Date(latestPost.published_at).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <section className="py-16 md:py-24 bg-card">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            Latest from the Blog
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            Insights, tutorials, and thoughts on eLearning and development.
          </p>
        </motion.div>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <Link
            to={`/blog/${latestPost.slug}`}
            className="group block overflow-hidden rounded-xl bg-background border border-border hover:border-primary/50 transition-all duration-300"
          >
            {/* Image */}
            <div className="relative aspect-video overflow-hidden bg-muted">
              <BlurImage
                src={latestPost.featured_image}
                alt={latestPost.title}
                width={latestPost.featured_image_width || undefined}
                height={latestPost.featured_image_height || undefined}
                objectFit={(latestPost.featured_image_fit as 'cover' | 'contain') || 'cover'}
                containerClassName="w-full h-full"
                className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 768px) 100vw, 800px"
              />
              <div className="absolute top-4 left-4">
                <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm">
                  {latestPost.category}
                </Badge>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 md:p-8">
              {/* Meta */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                <span className="inline-flex items-center gap-1">
                  <Calendar size={14} />
                  {formattedDate}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock size={14} />
                  {latestPost.read_time} min read
                </span>
              </div>

              {/* Title */}
              <h3 className="text-xl md:text-2xl font-semibold text-foreground group-hover:text-primary transition-colors">
                {latestPost.title}
              </h3>

              {/* Excerpt */}
              <HtmlContent
                content={latestPost.excerpt}
                className="mt-3 text-muted-foreground line-clamp-2 [&_p]:m-0"
              />

              {/* Read more */}
              <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary">
                Read article
                <ArrowUpRight
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </div>
            </div>
          </Link>
        </motion.article>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Button asChild size="lg" variant="outline">
            <Link to="/blog">
              View All Posts <ArrowRight size={18} className="ml-2" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
